import {Box, Typography} from '@mui/material';
import AurButton from '../common/aur-button/AurButton';
import {t} from 'i18next';
import {ChangeEvent, useEffect, useState} from 'react';
import {AurLoadingButton} from '../common/aur-loading-button/AurLoadingButton';
import {AurTextField} from '../common/aur-text-field/AurTextField';
import {MAX_CHAR} from '../../constants/constants';
import TeamMemberList from './TeamMemberList';
import {
  UploadDocumentInterface,
  UserData,
} from '../../types/concierge-reports/conciergeReportsType';
import {uploadConciergeReportsFile} from '../../api/ReportingManagement';
import {useToast} from '../../contexts/toastContext/ToastContext';
import {getArrayOfUserIdsList} from '../../utils/utils';
import {selectUser, UserInfoType} from '../../redux/slices/userSlice';
import {useSelector} from 'react-redux';
import {AxiosError} from 'axios';

interface UploadProps {
  uploadDocumentData: UploadDocumentInterface;
  onCancelClick: () => void;
  onEditClick: (description: string) => void;
  handleUploadSuccess: () => void;
}

const UploadConciergeReportFinalStep: React.FC<UploadProps> = ({
  uploadDocumentData,
  onCancelClick,
  onEditClick,
  handleUploadSuccess,
}) => {
  const [showButtonLoader, setShowButtonLoader] = useState<boolean>(false);
  const [descriptionValue, setDescriptionValue] = useState<string>(
    uploadDocumentData?.description ?? ''
  );
  const [isEditing, setIsEditing] = useState(false);
  const [updatedTeamMember, setUpdatedTeamMember] = useState<UserData[]>();

  const {showToast} = useToast();
  const userData: UserInfoType | null = useSelector(selectUser);

  useEffect(() => {
    if (uploadDocumentData?.teamMembers) {
      if (userData) {
        const updatedTeamMemberList: UserData[] = [
          {
            userId: userData.userId,
            name: userData.name,
            isOwner: true,
          },
          ...(uploadDocumentData?.teamMembers?.map((user) => ({
            userId: user.id as string,
            name: user.name as string,
          })) || []),
        ];
        setUpdatedTeamMember(updatedTeamMemberList);
      } else {
        const updatedTeamMemberList: UserData[] = [
          ...(uploadDocumentData?.teamMembers?.map((user) => ({
            userId: user.id as string,
            name: user.name as string,
          })) || []),
        ];
        setUpdatedTeamMember(updatedTeamMemberList);
      }
    }
  }, [uploadDocumentData]);

  const uploadHeaderHeaderAndButton = () => {
    return (
      <Box className="upload-reports-header">
        <Box className="upload-reports-header-details">
          <Typography className="typo-text headline2_size custom-header">
            {t('conciergeReports.uploadReports.header')}
          </Typography>
          <Typography className="typo-text body3_size custom-text-color">
            {uploadDocumentData?.fileName}
          </Typography>
        </Box>
        <Box className="upload-reports-buttons">
          <AurButton
            classNames="cancel-button"
            label={t('buttons.cancel')}
            type="exit"
            size="Large"
            title={t('buttons.cancel')}
            disabled={showButtonLoader}
            onClick={onCancelClick}
            dataTestId="upload-cancel-button"
          />
          <AurLoadingButton
            classNames="upload-button"
            label={t('buttons.uploadReport')}
            size="Large"
            title={t('buttons.uploadReport')}
            loading={showButtonLoader}
            onClick={uploadReportFile}
            dataTestId="upload-button"
          />
        </Box>
      </Box>
    );
  };

  const uploadReportFile = async () => {
    const payloadObj = {
      clientId: uploadDocumentData.client.id.toString(),
      projectId: uploadDocumentData.project.id.toString(),
      userIdList: getArrayOfUserIdsList(updatedTeamMember || []),
      fileName: uploadDocumentData?.fileName,
      file: uploadDocumentData.file,
      description: descriptionValue,
    };

    setShowButtonLoader(true);
    try {
      await uploadConciergeReportsFile(payloadObj);

      showToast(
        t('conciergeReports.uploadReports.uploadReportSuccess'),
        'success',
        undefined,
        'center'
      );
      setShowButtonLoader(false);
      handleUploadSuccess();
    } catch (error: unknown) {
      const err = error as AxiosError;
      showToast(err.message, 'error', undefined, 'center');
      setShowButtonLoader(false);
    }
  };

  const handleEditClick = () => {
    onEditClick(descriptionValue);
  };

  const uploadReportsContent = () => {
    // Handle input changes
    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      setDescriptionValue(event.target.value);
    };

    // Handle focus and blur events to set editing state
    const handleFocus = () => {
      setIsEditing(true);
    };

    const handleBlur = () => {
      setIsEditing(false);
    };

    return (
      <Box className="upload-reports-content">
        <Box className="upload-reports-content-desc">
          <Typography className="typo-text subhead2_size">
            {t('conciergeReports.uploadReports.uploadDesc')}
          </Typography>
          <Box>
            <AurTextField
              onChange={handleChange}
              variant="filled"
              className="aur-textfield"
              label={
                !isEditing && descriptionValue.length === 0
                  ? t('conciergeReports.uploadReports.addDescription')
                  : t('conciergeReports.uploadReports.projectDescription')
              }
              inputProps={{
                maxLength: MAX_CHAR,
              }}
              maxRows={5}
              fullWidth
              multiline
              value={descriptionValue}
              onFocus={handleFocus}
              onBlur={handleBlur}
              disabled={showButtonLoader}
              ariaLabel={!isEditing && descriptionValue.length === 0
                  ? t('conciergeReports.uploadReports.addDescription')
                  : t('conciergeReports.uploadReports.projectDescription')}
              sx={{
                '& .MuiInputBase-root': {
                  resize: 'both',
                  overflow: showButtonLoader ? 'none' : 'auto',
                  display: 'flex',
                  alignItems: 'flex-start',
                },
              }}
              data-testid="upload-description-textfield"
            />
            <Typography className="typo-text label2_size custom-text-style">
              {isEditing || descriptionValue.length === 0
                ? `${t('conciergeReports.uploadReports.charactersRemaining')}${MAX_CHAR - descriptionValue.length}`
                : `${descriptionValue.length}/${MAX_CHAR}`}
            </Typography>
          </Box>
        </Box>
        <Box className="upload-reports-content-client">
          <Box className="upload-reports-divider"></Box>
          <Box className="upload-reports-subContent">
            <Typography className="typo-text subhead2_size">
              {t('conciergeReports.uploadReports.reportDetails')}
            </Typography>
            {!showButtonLoader ? (
              <AurButton
                classNames="edit-button"
                label={t('conciergeReports.uploadReports.edit')}
                type="exit"
                size="Medium"
                title={t('conciergeReports.uploadReports.edit')}
                onClick={handleEditClick}
                dataTestId="upload-edit-button"
              />
            ) : null}
          </Box>
          <Box>
            <Typography className="typo-text label2_size custom-text-color">
              {t('conciergeReports.detailsPanel.client')}&nbsp;.&nbsp;
              {t('conciergeReports.detailsPanel.project')}
            </Typography>
            <Typography className="typo-text body1_size custom-text-project">
              {uploadDocumentData?.client?.name}&nbsp;.&nbsp;
              {uploadDocumentData?.project?.id}&nbsp;|&nbsp;
              {uploadDocumentData?.project?.name}
            </Typography>
          </Box>
          <Box className="upload-reports-user-list">
            <TeamMemberList
              userList={updatedTeamMember ?? []}
              client={uploadDocumentData?.client?.name as string}
            />
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box component="main">
      {uploadHeaderHeaderAndButton()}
      {uploadReportsContent()}
    </Box>
  );
};

export default UploadConciergeReportFinalStep;
